import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { formatDateTime } from "../utils/format";
import TableView from "../components/TableView";
import Pagination from "../components/Pagination";
import {
    Paper, Stack, Button, TextField, MenuItem, Typography, Box, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton, Chip, Switch, FormControlLabel
} from "@mui/material";
import { Email, Edit } from "@mui/icons-material";
import useDataFetch from "../hooks/useDataFetch";
import axiosInstance from "../utils/axiosInstance";

const roles = [
    { value: "admin", label: "管理者" },
    { value: "manager", label: "承認者" },
    { value: "staff", label: "スタッフ" },
    { value: "picking", label: "ピッキング" },
    { value: "sales", label: "営業" },
];

const statuses = [
    { value: "pending", label: "無効" },
    { value: "active", label: "有効" },
    { value: "invalid", label: "無効" },
];
const UsersPage = ({ getLimit }) => {
    const { showSnackbar } = useSnackbar();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const { dataset, error, currentPage, totalPages, handlePageChange, handleSearchSubmit } = useDataFetch("/users", {});
    const { handleSubmit, register, control, setValue, watch, reset, formState: { errors } } = useForm();

    // モーダル開閉
    const handleDialogOpen = (user = null) => {
        setEditingUser(user);
        reset(user || {
            username: "",
            email: "",
            first_name: "",
            last_name: "",
            phone_number: "",
            role: "",
            status: "active",
        });
        setOpenDialog(true);
    };

    const handleDialogClose = () => setOpenDialog(false);

    // 追加・編集保存
    const onSubmit = async (data) => {
        if (saving) return;
        setSaving(true);
        try {
            if (editingUser?.user_id) {
                await axiosInstance.put(`/users/${editingUser.user_id}`, data);
            } else {
                await axiosInstance.post(`/users`, data);
            }
            handleSearchSubmit?.();
            setOpenDialog(false);
            showSnackbar('保存しました', 'success');
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '保存に失敗しました', 'error');;
        } finally {
            setSaving(false);
        }
    };

    // モーダル内メール送信
    const handleSendEmail = async () => {
        if (sendingEmail || !editingUser?.user_id) return;
        setSendingEmail(true);
        try {
            const res = await axiosInstance.post(`/users/${editingUser.user_id}/send-setup-email`);
            setOpenDialog(false);
            showSnackbar(res?.data?.message || 'パスワード設定メールを送信しました', 'success');
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || 'メールの送信に失敗しました', 'error');;
        } finally {
            setSendingEmail(false);
        }
    };
    const roleLabelMap = Object.fromEntries(
        roles.map(r => [r.value, r.label])
    );
    const statusLabelMap = Object.fromEntries(
        statuses.map(s => [s.value, s.label])
    );
    // テーブル列定義
    const columns = [
        {
            label: "ユーザ名", accessor: "username",
            render: item => (
                <Typography>{item.username}</Typography>
            ), width: "180px"
        },
        {
            label: "メールアドレス",
            accessor: "email",
            width: "260px",
            render: item => (
                <Typography>{item.email}</Typography>
            ),
        },
        {
            label: "名前",
            accessor: "first_name",
            render: item => (
                <Typography>{item.last_name} {item.first_name}</Typography>
            ), width: "150px"
        },
        {
            label: "権限",
            accessor: "role",
            render: item => (
                <Typography>{roleLabelMap[item.role] || item.role}</Typography>
            ),
            width: "100px"
        },
        {
            label: "ステータス",
            accessor: "status",
            width: "80px",
            render: item => {
                const colorMap = {
                    active: "success",
                };

                return (
                    <Chip
                        label={statusLabelMap[item.status] || item.status}
                        color={colorMap[item.status] || "default"}
                    />
                );
            },
        },
        {
            label: "最終ログイン", accessor: "last_login_at", width: "160px",
            render: item => (
                formatDateTime(item.last_login_at)
            )
        },
        {
            label: '',
            accessor: "actions",
            render: item => (
                <Tooltip title="編集">
                    <IconButton size="small" onClick={() => handleDialogOpen(item)}>
                        <Edit />
                    </IconButton>
                </Tooltip>
            ),
            width: "50px"
        }
    ];
    const maxUsers = getLimit?.('max_users') ?? 0;

    if (error) return <GlobalError errorMessage={error} />;
    return (
        <>
            <Box
                spacing={1}
                direction="row"
                alignItems="end"
                justifyContent="space-between"
                sx={{ display: 'flex' }}
            >
                <Typography variant="h1" component="h1">ユーザ管理</Typography>
                <Button variant="contained"
                    onClick={() => handleDialogOpen(null)}
                    sx={{ mb: 1 }}>ユーザ追加</Button>
            </Box>
            <Paper variant="section">
                {maxUsers <= 1 ? (
                    <Alert severity="info" sx={{ mb: 1 }}>
                        プランをアップグレードすると追加できます
                    </Alert>
                ) : dataset.length >= maxUsers ? (
                    <Alert severity="info" sx={{ mb: 1 }}>
                        ライセンス追加は営業担当にお問合わせください
                    </Alert>
                ) : null}
                <Alert severity="info" sx={{ mb: 2 }}>
                    ステータスが「有効」の場合、システムにログインできます
                </Alert>
                <TableView dataset={dataset} columns={columns} keyColumn="user_id" />
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </Paper>

            {/* 追加・編集モーダル */}
            <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="sm">
                <DialogTitle>{editingUser?.user_id ? "ユーザ編集" : "ユーザ追加"}</DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent>
                        <Stack spacing={2} mt={1}>
                            {!editingUser?.user_id &&
                                <Alert severity="info">
                                    「保存」すると、ユーザにパスワード設定メールを送付します。
                                </Alert>
                            }
                            <TextField
                                label="ユーザ名"
                                {...register("username", { required: "必須です", minLength: { value: 3, message: "3文字以上" }, maxLength: { value: 255, message: "255文字以内" } })}
                                error={!!errors.username}
                                helperText={errors.username?.message}
                                inputProps={{ maxLength: 255 }}
                            />
                            <TextField
                                label="メール"
                                {...register("email", { required: "必須です", maxLength: { value: 255, message: "255文字以内で入力してください" }, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "メールアドレスの形式が正しくありません" } })}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                inputProps={{ maxLength: 255 }}
                            />
                            <TextField
                                label="姓"
                                {...register("last_name", { required: "必須です", maxLength: { value: 100, message: "100文字以内で入力してください" } })}
                                error={!!errors.last_name}
                                helperText={errors.last_name?.message}
                                inputProps={{ maxLength: 100 }}
                            />
                            <TextField
                                label="名"
                                {...register("first_name", { required: "必須です", maxLength: { value: 100, message: "100文字以内で入力してください" } })}
                                error={!!errors.first_name}
                                helperText={errors.first_name?.message}
                                inputProps={{ maxLength: 100 }}
                            />
                            <TextField
                                label="電話番号"
                                {...register("phone_number", { maxLength: { value: 100, message: "20文字以内で入力してください" }, pattern: { value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/, message: "電話番号の形式が正しくありません" } })}
                                error={!!errors.phone_number}
                                helperText={errors.phone_number?.message}
                                inputProps={{ maxLength: 20 }}
                            />
                            <Controller
                                name="role"
                                control={control}
                                rules={{ required: "必須です" }}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        label="権限"
                                        {...field}  // value, onChange がここでセットされる
                                        error={!!errors.role}
                                        helperText={errors.role?.message}
                                    >
                                        <MenuItem value="">選択してください</MenuItem>
                                        {roles.map(r => (
                                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                            {editingUser?.user_id && (
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    {...field}
                                                    checked={field.value === "active"}
                                                    onChange={(e) => field.onChange(e.target.checked ? "active" : "invalid")}
                                                />
                                            }
                                            label={field.value === "active" ? "有効" : "無効"}
                                        />
                                    )}
                                />
                            )}

                            {editingUser?.user_id && editingUser?.status === 'active' && (
                                <Button variant="outlined" startIcon={<Email />} onClick={handleSendEmail} disabled={sendingEmail} >
                                    パスワード設定メール送信
                                </Button>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose} variant="outlined">キャンセル</Button>
                        <Button variant="contained" type="submit" disabled={saving}>保存</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
};

export default UsersPage;
